import { useState } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'
import ProviderConfigForm from './ProviderConfigForm'

export default function ProviderConfigsTab() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingConfig, setEditingConfig] = useState(null)
  const [deletingConfig, setDeletingConfig] = useState(null)
  const [selectedConfig, setSelectedConfig] = useState(null)

  const { data: configs, loading, error, refetch } = useSupabaseQuery('WhatsAppProviderConfigs', {
    order: { column: 'Name', ascending: true }
  })

  const { data: providers } = useSupabaseQuery('WhatsAppProviders', {
    order: { column: 'WhatsAppProviderName', ascending: true }
  })

  const { insert, update, remove, loading: mutating } = useSupabaseMutation('WhatsAppProviderConfigs')

  const getProviderName = (providerId) => {
    return providers?.find(p => p.WhatsAppProviderId === providerId)?.WhatsAppProviderName || 'Unknown'
  }

  const handleCreate = async (formData) => {
    const { data, error } = await insert(formData)
    if (!error && data) {
      setShowCreateModal(false)
      refetch()
    }
    return { data, error }
  }

  const handleUpdate = async (formData) => {
    const { data, error } = await update(
      editingConfig.WhatsAppProviderConfigId,
      formData,
      'WhatsAppProviderConfigId'
    )
    if (!error) {
      setEditingConfig(null)
      if (selectedConfig?.WhatsAppProviderConfigId === editingConfig.WhatsAppProviderConfigId) {
        setSelectedConfig({ ...selectedConfig, ...formData })
      }
      refetch()
    }
    return { data, error }
  }

  const handleDelete = async () => {
    const { error } = await remove(deletingConfig.WhatsAppProviderConfigId, 'WhatsAppProviderConfigId')
    if (!error) {
      if (selectedConfig?.WhatsAppProviderConfigId === deletingConfig.WhatsAppProviderConfigId) {
        setSelectedConfig(null)
      }
      setDeletingConfig(null)
      refetch()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Provider Configurations</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Manage WhatsApp Business API provider configs
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Add Provider Config</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configs List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Configs ({configs?.length || 0})
              </h3>
            </div>
            {configs && configs.length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
                {configs.map((config) => (
                  <li
                    key={config.WhatsAppProviderConfigId}
                    onClick={() => setSelectedConfig(config)}
                    className={`p-4 cursor-pointer transition ${
                      selectedConfig?.WhatsAppProviderConfigId === config.WhatsAppProviderConfigId
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {config.Name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {getProviderName(config.WhatsAppProviderId)}
                        </p>
                      </div>
                      {selectedConfig?.WhatsAppProviderConfigId === config.WhatsAppProviderConfigId && (
                        <svg className="w-5 h-5 text-primary-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No provider configs</p>
              </div>
            )}
          </div>
        </div>

        {/* Config Details */}
        <div className="lg:col-span-2">
          {selectedConfig ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {selectedConfig.Name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Provider: {getProviderName(selectedConfig.WhatsAppProviderId)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">
                    Config ID: {selectedConfig.WhatsAppProviderConfigId}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditingConfig(selectedConfig)}
                    className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition"
                    title="Edit"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDeletingConfig(selectedConfig)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                {/* JSON Config */}
                {selectedConfig.JsonConfig && Object.keys(selectedConfig.JsonConfig).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Configuration
                    </label>
                    <pre className="text-xs text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-4 rounded overflow-x-auto">
                      {JSON.stringify(selectedConfig.JsonConfig, null, 2)}
                    </pre>
                  </div>
                )}

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <p className="ml-3 text-sm text-blue-800 dark:text-blue-200">
                      This provider configuration is used to send WhatsApp messages via the {getProviderName(selectedConfig.WhatsAppProviderId)} API.
                      The JsonConfig typically contains AccessToken, PhoneNumberId, and GraphBase URL.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Config Selected</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Select a provider config from the list to view details
              </p>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <Modal title="Add Provider Configuration" onClose={() => setShowCreateModal(false)}>
          <ProviderConfigForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={mutating}
          />
        </Modal>
      )}

      {editingConfig && (
        <Modal title="Edit Provider Configuration" onClose={() => setEditingConfig(null)}>
          <ProviderConfigForm
            config={editingConfig}
            onSubmit={handleUpdate}
            onCancel={() => setEditingConfig(null)}
            loading={mutating}
          />
        </Modal>
      )}

      {deletingConfig && (
        <Modal title="Delete Provider Config" onClose={() => setDeletingConfig(null)} size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{deletingConfig.Name}</strong>? Any accounts using this config will lose their provider connection.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingConfig(null)}
                disabled={mutating}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center space-x-2"
              >
                {mutating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
